import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Typography, Box, Paper, Grid2, Card, CardContent,
  List, ListItem, ListItemText, ListItemIcon, Chip, CircularProgress,
  Collapse, IconButton, Divider, Tooltip
} from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';

const OFFLINE_THRESHOLD_MS = 605 * 1000; // 605秒视为离线

// ── 与 Sensors.js 相同的告警阈值逻辑 ──────────────────────────────────
function calcAlarms(data, timeSinceUpdateMs) {
  const offline = timeSinceUpdateMs > OFFLINE_THRESHOLD_MS;

  const battery_low = data.scd?.v != null && data.scd.v <= 3.93;
  const battery_critical = data.scd?.v != null && data.scd.v <= 3.57;

  const scd_co2_alarm      = data.scd?.["3"] > 1000;
  const scd_temp_alarm     = (data.scd?.["4"] / 100 > 30) || (data.scd?.["4"] / 100 < 15);
  const scd_humdity_alarm  = (data.scd?.["5"] / 100 > 70) || (data.scd?.["5"] / 100 < 30);

  function getHumidityRatio(temp_celsius, rh) {
    if (temp_celsius == null || rh == null) return null;
    const Ps = 6.11 * Math.pow(10, (7.5 * temp_celsius) / (237.3 + temp_celsius));
    const Pw = (rh / 100) * Ps;
    return 0.621945 * (Pw / (1009.01 - Pw)) * 1000;
  }
  const hr_scd = getHumidityRatio(data.scd?.["4"] / 100, data.scd?.["5"] / 100);
  const hr_sfa = getHumidityRatio(data.sfa?.["4"] / 100, data.sfa?.["5"] / 100);
  const scd_hr_alarm = hr_scd > 10;
  const scd_alarm = scd_co2_alarm || scd_temp_alarm || scd_humdity_alarm || scd_hr_alarm;

  const sfa_hcho_alarm    = data.sfa?.["3"] > 200;
  const sfa_temp_alarm    = data.sfa?.["4"] / 100 > 30;
  const sfa_humdity_alarm = data.sfa?.["5"] / 100 > 80;
  const sfa_hr_alarm      = hr_sfa > 10;
  const sfa_alarm = sfa_hcho_alarm || sfa_temp_alarm || sfa_humdity_alarm || sfa_hr_alarm;

  const sps_pm25_alarm = data.sps?.["1"] / 100 > 20;
  const sps_pm10_alarm = data.sps?.["3"] / 100 > 20;
  const sps_alarm = sps_pm25_alarm || sps_pm10_alarm ||
    (data.sps?.["0"] / 100 > 20) || (data.sps?.["2"] / 100 > 20);

  const hasAlarm = scd_alarm || sfa_alarm || sps_alarm;

  // 构建告警详情列表
  const alarmDetails = [];
  if (offline)                alarmDetails.push('离线 (超过 10 分钟未更新)');
  if (battery_critical)       alarmDetails.push('电池严重不足 (≤3.57V)');
  else if (battery_low)       alarmDetails.push('电池电量低 (≤3.93V)');
  if (scd_co2_alarm)          alarmDetails.push(`CO₂ 超标 (${data.scd["3"]} ppm > 1000)`);
  if (scd_temp_alarm)         alarmDetails.push(`温度异常 (${(data.scd["4"]/100).toFixed(1)}°C)`);
  if (scd_humdity_alarm)      alarmDetails.push(`湿度异常 (${(data.scd["5"]/100).toFixed(1)}%)`);
  if (scd_hr_alarm)           alarmDetails.push(`湿度比异常 (${hr_scd?.toFixed(1)} g/kg)`);
  if (sfa_hcho_alarm)         alarmDetails.push(`甲醛超标 (${data.sfa["3"]} ppb > 200)`);
  if (sps_pm25_alarm)         alarmDetails.push(`PM2.5 超标 (${(data.sps["1"]/100).toFixed(1)} μg/m³)`);
  if (sps_pm10_alarm)         alarmDetails.push(`PM10 超标 (${(data.sps["3"]/100).toFixed(1)} μg/m³)`);

  return { offline, battery_low, battery_critical, hasAlarm, alarmDetails };
}

// ── 单条设备行 ────────────────────────────────────────────────────────
function DeviceRow({ sensorId, data, lastUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const timeSinceUpdateMs = Date.now() - (lastUpdated || 0);
  const timeSec = Math.floor(timeSinceUpdateMs / 1000);
  const { offline, battery_critical, battery_low, hasAlarm, alarmDetails } = calcAlarms(data, timeSinceUpdateMs);

  const statusColor = offline ? '#888' : hasAlarm ? '#cc3333' : '#2e7d32';
  const statusLabel = offline ? '离线' : hasAlarm ? '告警' : '正常';
  const StatusIcon  = offline ? SignalWifiOffIcon : hasAlarm ? WarningAmberIcon : CheckCircleIcon;

  return (
    <Paper variant="outlined" sx={{ mb: 1, borderColor: offline ? '#555' : hasAlarm ? '#993333' : '#2e7d32', borderRadius: 2 }}>
      <ListItem
        sx={{ cursor: (hasAlarm || offline) ? 'pointer' : 'default', py: 1 }}
        onClick={() => (hasAlarm || offline) && setExpanded(e => !e)}
        secondaryAction={
          (hasAlarm || offline) ? (
            <IconButton size="small" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          ) : null
        }
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <StatusIcon sx={{ color: statusColor }} />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="body1" fontWeight={500}>{data.name || sensorId}</Typography>
              {battery_critical && <Chip icon={<BatteryAlertIcon />} label="电池严重不足" size="small" color="error" />}
              {battery_low && !battery_critical && <Chip icon={<BatteryAlertIcon />} label="电池低" size="small" color="warning" />}
            </Box>
          }
          secondary={
            <Box display="flex" gap={1} alignItems="center" mt={0.3} flexWrap="wrap">
              <Chip label={statusLabel} size="small"
                sx={{ bgcolor: statusColor, color: '#fff', fontWeight: 600, height: 20, fontSize: 11 }} />
              <Typography variant="caption" color="text.secondary">
                {offline ? `最后更新: ${Math.floor(timeSec / 60)}分钟前` : `${timeSec}秒前更新`}
              </Typography>
              {data.scd?.["3"] != null &&
                <Typography variant="caption" color="text.secondary">CO₂: {data.scd["3"]}ppm</Typography>}
              {data.sps?.["1"] != null &&
                <Typography variant="caption" color="text.secondary">PM2.5: {(data.sps["1"]/100).toFixed(1)}μg/m³</Typography>}
            </Box>
          }
        />
      </ListItem>

      {/* 告警详情展开区 */}
      <Collapse in={expanded && alarmDetails.length > 0}>
        <Divider />
        <Box px={2} py={1} bgcolor="rgba(180,50,50,0.08)" borderRadius="0 0 8px 8px">
          <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
            告警详情
          </Typography>
          {alarmDetails.map((msg, idx) => (
            <Box key={idx} display="flex" alignItems="center" gap={0.5} mb={0.3}>
              <ErrorIcon sx={{ fontSize: 14, color: '#cc3333' }} />
              <Typography variant="caption" color="error">{msg}</Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── 统计卡片 ─────────────────────────────────────────────────────────
function StatCard({ count, label, color, icon: Icon, onClick, clickable }) {
  return (
    <Card
      variant="outlined"
      onClick={clickable ? onClick : undefined}
      sx={{
        borderColor: color, borderWidth: 2, borderRadius: 2,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'transform 0.15s',
        '&:hover': clickable ? { transform: 'scale(1.03)', boxShadow: 4 } : {},
        bgcolor: '#1e1e1e',
      }}
    >
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        <Icon sx={{ fontSize: 36, color, mb: 0.5 }} />
        <Typography variant="h3" fontWeight={700} sx={{ color }}>
          {count}
        </Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {clickable && count > 0 &&
          <Typography variant="caption" color="text.secondary">点击查看</Typography>}
      </CardContent>
    </Card>
  );
}

// ── 主页面 ────────────────────────────────────────────────────────────
export default function DeviceStatus() {
  const [sensorData, setSensorData]     = useState({});
  const [lastUpdated, setLastUpdated]   = useState({});
  const [loading, setLoading]           = useState(true);
  const [lastRefresh, setLastRefresh]   = useState(null);
  const [activeFilter, setActiveFilter] = useState(null); // null | 'normal' | 'alarm' | 'offline'

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch('/api/sensor/lastrecord');
      const data = await response.json();
      if (data.error) return;

      const newSensorData = {};
      const newLastUpdated = {};

      Object.entries(data).forEach(([sensorId, record]) => {
        if (record.name === record.deviceId) return; // 未配置设备跳过
        const SCDData = record.SCD ? JSON.parse(record.SCD) : null;
        const SFAData = record.SFA ? JSON.parse(record.SFA) : null;
        const SPSData = record.SPS ? JSON.parse(record.SPS) : null;
        newSensorData[sensorId] = { name: record.name, scd: SCDData, sfa: SFAData, sps: SPSData, ...record };
        newLastUpdated[sensorId] = new Date(record.timestamp).getTime();
      });

      setSensorData(newSensorData);
      setLastUpdated(newLastUpdated);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('DeviceStatus fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // 分类统计
  const now = Date.now();
  const classified = Object.entries(sensorData).map(([sensorId, data]) => {
    const timeSinceUpdateMs = now - (lastUpdated[sensorId] || 0);
    const { offline, hasAlarm } = calcAlarms(data, timeSinceUpdateMs);
    return { sensorId, data, offline, hasAlarm, timeSinceUpdateMs };
  });

  const offlineList = classified.filter(d => d.offline);
  const alarmList   = classified.filter(d => !d.offline && d.hasAlarm);
  const normalList  = classified.filter(d => !d.offline && !d.hasAlarm);

  // 当前显示列表
  const displayList =
    activeFilter === 'normal'  ? normalList :
    activeFilter === 'alarm'   ? alarmList :
    activeFilter === 'offline' ? offlineList :
    [...alarmList, ...offlineList, ...normalList]; // 默认：告警/离线优先

  const containerStyle = {
    maxHeight: window.innerWidth < 500 ? '70vh' : '77vh',
    overflowY: 'auto',
    padding: '10px',
  };

  return (
    <Container style={containerStyle}>
      <Box mt={2} mb={2} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" fontWeight={600}>设备状态监控</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {lastRefresh &&
            <Typography variant="caption" color="text.secondary">
              {lastRefresh.toLocaleTimeString()} 更新
            </Typography>}
          <Tooltip title="立即刷新">
            <IconButton size="small" onClick={fetchData} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* 统计卡片 */}
      <Grid2 container spacing={2} mb={3}>
        <Grid2 size={{ xs: 4 }}>
          <StatCard
            count={normalList.length} label="正常" color="#2e7d32"
            icon={CheckCircleIcon} clickable={normalList.length > 0}
            onClick={() => setActiveFilter(activeFilter === 'normal' ? null : 'normal')}
          />
        </Grid2>
        <Grid2 size={{ xs: 4 }}>
          <StatCard
            count={alarmList.length} label="数据告警" color="#cc3333"
            icon={WarningAmberIcon} clickable={alarmList.length > 0}
            onClick={() => setActiveFilter(activeFilter === 'alarm' ? null : 'alarm')}
          />
        </Grid2>
        <Grid2 size={{ xs: 4 }}>
          <StatCard
            count={offlineList.length} label="离线" color="#888"
            icon={SignalWifiOffIcon} clickable={offlineList.length > 0}
            onClick={() => setActiveFilter(activeFilter === 'offline' ? null : 'offline')}
          />
        </Grid2>
      </Grid2>

      {/* 过滤标签 */}
      {activeFilter && (
        <Box mb={1} display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            当前筛选：
          </Typography>
          <Chip
            label={activeFilter === 'normal' ? '正常设备' : activeFilter === 'alarm' ? '数据告警设备' : '离线设备'}
            onDelete={() => setActiveFilter(null)}
            size="small"
            color={activeFilter === 'normal' ? 'success' : activeFilter === 'alarm' ? 'error' : 'default'}
          />
        </Box>
      )}

      {/* 设备列表 */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : displayList.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <SensorsIcon sx={{ fontSize: 48, opacity: 0.3 }} />
          <Typography color="text.secondary">暂无设备数据</Typography>
        </Paper>
      ) : (
        <List disablePadding>
          {displayList.map(({ sensorId, data }) => (
            <DeviceRow
              key={sensorId}
              sensorId={sensorId}
              data={data}
              lastUpdated={lastUpdated[sensorId]}
            />
          ))}
        </List>
      )}
    </Container>
  );
}
